import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveCustomerSignInPath } from '../auth/customerAuthPaths.js';
import {
  parkPaymentResumeReturnTarget,
  type ReturnTargetSource,
} from '../../shared/session/returnTarget.js';

export interface CheckoutGuestSignInPromptProps {
  readonly tenantCode: string;
  readonly source?: ReturnTargetSource;
}

/**
 * Guest payment / donation: offer Sign in beside receipt-email capture.
 * Parks pathname+search (incl. sessionId) then path-tenant `/{tenant}/sign-in`
 * so post-auth consume restores the in-progress checkout (platform `/sign-in`
 * never reads per-tenant parks — G4).
 * G1 — navigate only when park returns non-null; null stays on checkout with
 * recoverable aria-live feedback.
 */
export function CheckoutGuestSignInPrompt({
  tenantCode,
  source = 'checkout',
}: CheckoutGuestSignInPromptProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const location = useLocation();
  const [parkFailed, setParkFailed] = useState(false);

  return (
    <>
      <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
        {t('checkout.guestSignInPrompt')}{' '}
        <button
          type="button"
          className="text-[var(--color-accent)] underline"
          data-testid="customer-checkout-guest-sign-in"
          onClick={() => {
            const parked = parkPaymentResumeReturnTarget(
              `${location.pathname}${location.search}`,
              { tenantCode, source },
            );
            if (parked === null) {
              setParkFailed(true);
              return;
            }
            setParkFailed(false);
            navigate(resolveCustomerSignInPath(tenantCode));
          }}
        >
          {t('checkout.guestSignInCta')}
        </button>
      </p>
      {parkFailed ? (
        <p
          role="status"
          aria-live="polite"
          className="mt-1 text-sm text-[var(--color-on-surface-muted)]"
          data-testid="customer-checkout-guest-sign-in-park-failed"
        >
          {t('checkout.guestSignInParkFailed', {
            defaultValue:
              'Could not save your checkout progress. Stay here and try Sign in again.',
          })}
        </p>
      ) : null}
    </>
  );
}
